import type {
  AllowedStatesData,
  AllowedStatesQueryParams,
  AllowedStatesRequestData
} from '@library/types/allowedStates';
import { useCallback, useMemo } from 'react';
import {
  useCreateAllowedStateMutation,
  useGetAllowedStatesQuery,
  useUpdateAllowedStateMutation
} from '@bo-services/allowedStatesApi';
import { formatDate } from '@library/component-utils/dateTime';

export const useAllowedStates = ({
  allowedStatesQueryParams,
  currentItemId,
  setSubmitMessage
}: {
  allowedStatesQueryParams: AllowedStatesQueryParams;
  currentItemId: string;
  setSubmitMessage: (message: string) => void;
}) => {
  const {
    data: response,
    isError,
    isLoading,
    isSuccess
  } = useGetAllowedStatesQuery(allowedStatesQueryParams);

  const [createAllowedState] = useCreateAllowedStateMutation();
  const [updateAllowedState] = useUpdateAllowedStateMutation();

  const allowedStatesData = useMemo(() => {
    return response?.data.map((allowedState) => {
      return {
        id: allowedState.id,
        name: allowedState.attributes.name,
        state_code: allowedState.attributes.state_code,
        created_at: formatDate({ date: allowedState.attributes.created_at }),
        updated_at: formatDate({ date: allowedState.attributes.updated_at }),
        thirty_one_day_limit_cents:
          allowedState.attributes.thirty_one_day_limit_cents ?? 0,
        rules: allowedState.attributes.rules,
        f2p_only: allowedState.attributes.f2p_only,
        allow_payments: allowedState.attributes.allow_payments,
        allow_withdrawals: allowedState.attributes.allow_withdrawals,
        allow_entries: allowedState.attributes.allow_entries,
        allow_mastercard: allowedState.attributes.allow_mastercard,
        bypass_playthrough: allowedState.attributes.bypass_playthrough,
        default_game_mode:
          allowedState.relationships.default_game_mode.data?.id || '',
        allow_venmo: allowedState.attributes.allow_venmo,
        allow_paypal: allowedState.attributes.allow_paypal,
        allow_applepay: allowedState.attributes.allow_applepay,
        allow_bank: allowedState.attributes.allow_bank,
        allow_card: allowedState.attributes.allow_card,
        allow_visa: allowedState.attributes.allow_visa,
        allow_discover: allowedState.attributes.allow_discover,
        minimum_withdrawal_cents:
          allowedState.attributes.minimum_withdrawal_cents,
        allowed_state_game_modes:
          allowedState.relationships.game_modes.data.map((mode) => mode.id),
        game_modes: allowedState.relationships.game_modes.data.map(
          (mode) => mode.id
        )
      };
    }) as Array<AllowedStatesData>;
  }, [response]);

  const mapAllowedStatesDataToRequest = useCallback(
    (formData: Partial<AllowedStatesData>): AllowedStatesRequestData => {
      return {
        name: formData.name || '',
        state_code: formData.state_code || '',
        thirty_one_day_limit_cents:
          Number(formData.thirty_one_day_limit_cents) || 0,
        f2p_only: !!formData.f2p_only,
        allow_payments: !!formData.allow_payments,
        allow_withdrawals: !!formData.allow_withdrawals,
        allow_entries: !!formData.allow_entries,
        allow_mastercard: !!formData.allow_mastercard,
        bypass_playthrough: !!formData.bypass_playthrough,
        default_game_mode_id: Number(formData.default_game_mode) || 0,
        allow_venmo: !!formData.allow_venmo,
        allow_paypal: !!formData.allow_paypal,
        allow_applepay: !!formData.allow_applepay,
        allow_bank: !!formData.allow_bank,
        allow_card: !!formData.allow_card,
        allow_visa: !!formData.allow_visa,
        allow_discover: !!formData.allow_discover,
        minimum_withdrawal_cents:
          Number(formData.minimum_withdrawal_cents) || 0,
        combo_play_required: !!formData.rules?.combo_play_required,
        disallow_tailed_entries: !!formData.rules?.disallow_tailed_entries,
        disallow_in_game_lineups: !!formData.rules?.disallow_in_game_lineups,
        projection_minimum_cents_per_game_weekly_limit_cents: 0
      };
    },
    []
  );

  const handleUpdate = useCallback(
    async (formData: Partial<AllowedStatesData>) => {
      await updateAllowedState({
        id: currentItemId,
        data: mapAllowedStatesDataToRequest(formData)
      })
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Allowed State updated');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't update Allowed State - ${error}`);
        });
    },
    [
      updateAllowedState,
      currentItemId,
      mapAllowedStatesDataToRequest,
      setSubmitMessage
    ]
  );

  const handleCreate = useCallback(
    async (formData: Partial<AllowedStatesData>) => {
      await createAllowedState(mapAllowedStatesDataToRequest(formData))
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Allowed State created');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't create Allowed State - ${error}`);
        });
    },
    [createAllowedState, mapAllowedStatesDataToRequest, setSubmitMessage]
  );

  return {
    createAllowedState: handleCreate,
    isError,
    isLoading,
    isSuccess,
    mapAllowedStatesDataToRequest,
    meta: response?.meta || {},
    page: response?.meta.current_page,
    allowedStates: allowedStatesData || [],
    totalPages: response?.meta.total_pages || 0,
    updateAllowedState: handleUpdate
  };
};
